import React from 'react';
import { UtensilsCrossed, X, Camera, Save, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';

/**
 * DraftMealSheet — extracted from `pages/Dashboard.js` (Iter. 405.f).
 *
 * Presents the hybrid-meal cart (`useMealDraft.items`) so the user can:
 *   • review each ingredient with its macros
 *   • remove an item
 *   • scan another ingredient (re-opens the AddMealOptionsModal)
 *   • save the consolidated meal in ONE backend call
 *   • discard the entire cart
 *
 * Pure presentational component. The parent owns all state mutations
 * (`onRemoveItem`, `onScanAnother`, `onSave`, `onCancel`).
 */
export default function DraftMealSheet({
  open,
  onOpenChange,
  items,
  saving,
  language = 'fr',
  onRemoveItem,
  onScanAnother,
  onSave,
  onCancel,
}) {
  const totalProtein = items.reduce(
    (s, i) => s + (typeof i.protein_g === 'number' ? i.protein_g : parseFloat(i.protein_g) || 0),
    0,
  );
  const totalCalories = items.reduce(
    (s, i) => s + (typeof i.calories === 'number' ? i.calories : parseFloat(i.calories) || 0),
    0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <UtensilsCrossed className="w-5 h-5 text-primary" />
            {language === 'fr' ? 'Repas en cours' : 'Meal in progress'}
          </DialogTitle>
          <DialogDescription>
            {language === 'fr'
              ? `${items.length} ingredient${items.length > 1 ? 's' : ''} — Scanne un autre ou sauvegarde`
              : `${items.length} ingredient${items.length > 1 ? 's' : ''} — Scan another or save`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 p-2.5 rounded-lg bg-secondary/50 border border-border"
              data-testid={`draft-item-${item.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                <p className="text-[10px] text-muted-foreground">{item.portion_g}g</p>
              </div>
              <div className="flex items-center gap-2 text-xs shrink-0">
                <span className="text-primary font-bold">{item.protein_g}g</span>
                <span className="text-muted-foreground">{item.calories} cal</span>
              </div>
              <button
                onClick={() => onRemoveItem(item.id)}
                className="p-1 rounded hover:bg-destructive/20 text-muted-foreground/40 hover:text-destructive transition-colors shrink-0"
                aria-label="Retirer"
                data-testid={`draft-item-remove-${item.id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          {items.length > 0 && (
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
              <div className="flex justify-between text-sm">
                <span className="font-heading uppercase text-muted-foreground">Total</span>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">{totalProtein.toFixed(1)}g prot</span>
                  <span className="font-medium">{totalCalories.toFixed(0)} cal</span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2 pt-2">
            <Button
              onClick={onScanAnother}
              variant="outline"
              className="w-full border-primary/30 text-primary hover:bg-primary/10"
              data-testid="scan-another-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Scanner un autre ingredient' : 'Scan another ingredient'}
            </Button>
            <Button
              onClick={onSave}
              disabled={saving || items.length === 0}
              className="w-full bg-primary text-primary-foreground"
              data-testid="save-draft-meal-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {language === 'fr' ? 'Sauvegarder le repas complet' : 'Save complete meal'}
            </Button>
            <Button
              variant="ghost"
              onClick={onCancel}
              className="w-full text-muted-foreground text-xs"
              data-testid="draft-meal-cancel-btn"
            >
              {language === 'fr' ? 'Annuler et vider le panier' : 'Cancel and clear cart'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
